import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, Subscription } from 'rxjs';
import { AppEvents } from 'src/app/app.config/app-events';
import { ConstantesApp } from 'src/app/app.config/constantes-app';
import { Cliente } from 'src/app/app.domain.models/cliente';
import { Poliza } from 'src/app/app.domain.models/poliza';
import { PdfApiService } from 'src/app/app.domain.services/pdf.api.service';
import { SeguroApiService } from 'src/app/app.domain.services/seguro.api.service';
import { PolizaApiService } from 'src/app/app.modules/individual/domain.services/poliza.api.service';
import { Utilidades } from 'src/app/app.utilidades/utilidades';
import { EventBrokerService } from 'src/app/core.services/event-broker.service';
import { SesionService } from 'src/app/core.services/sesion.service';
import { ModalFallecimientoAccidenteComponent } from '../../modales/modal-fallecimiento-accidente/modal-fallecimiento-accidente.component';
import { ModalIncapacidadAccidenteComponent } from '../../modales/modal-incapacidad-accidente/modal-incapacidad-accidente.component';
import { ModalInformacionLegalComponent } from '../../modales/modal-informacion-legal/modal-informacion-legal.component';
import { FirmaOperacionService } from 'src/app/app.domain.services/operacion.service';

@Component({
    selector: 'app-ver-precio',
    templateUrl: './ver-precio.component.html',
    styleUrls: ['./ver-precio.component.scss'],
})
export class VerPrecioComponent implements OnInit {
    cliente!: Cliente;
    poliza = new Poliza();

    opcionesCapital: number[] = [50000, 100000, 150000, 200000, 250000, 300000];
    opcionesCapitalOriginales: number[] = [...this.opcionesCapital];

    formCapital!: FormGroup;

    mostrarInputOtroCapital: boolean = false;

    private suscripcionOtroCapital?: Subscription;

    maxCapital!: number;

    comprobarSesion = false;

    constructor(
        private sesion: SesionService,
        private pdfService: PdfApiService,
        private eventService: EventBrokerService,
        public dialog: MatDialog,
        private datePipe: DatePipe,
        private seguroService: SeguroApiService,
        private router: Router,
        private polizaService: PolizaApiService,
        private operacionService: FirmaOperacionService,
        private route: ActivatedRoute,
    ) {
        this.formCapital = new FormGroup({
            cobertura: new FormControl('', [Validators.required]),
            otroCapital: new FormControl('')
        });
    }

    ngOnInit() {
        this.route.queryParamMap.subscribe(params => {
            const tokenFirma = params.get('token');
            if (tokenFirma) {
                this.operacionService.obtenerDatosTokenCarritoAbandonado(tokenFirma).then((response: number) => {
                    this.polizaService.obtenerById(response).then((polizaResponse) => {
                        if (polizaResponse) {
                            this.poliza = polizaResponse;
                            this.cliente = this.poliza.cliente;
                            this.sesion.setCliente(this.cliente);
                            this.sesion.setPoliza(this.poliza);
                            this.rellenarFormulario();
                        }
                    });
                });
            } else {
                this.cliente = this.sesion.getCliente();
                this.poliza = this.sesion.getPoliza();
                this.rellenarFormulario();
            }
        });
    }

    rellenarFormulario() {
        this.comprobarPolizaSesion();

        if (this.comprobarSesion) {
            // Convertimos a Number porque llega como string
            const esOtroCapital = !this.opcionesCapital.includes(Number(this.poliza.capitalCobertura));
            this.maxCapital = this.obtenerMaximoCapitalPermitido();

            this.formCapital.removeControl('cobertura');
            this.formCapital.addControl('cobertura', new FormControl(esOtroCapital ? 'otro' : this.poliza.capitalCobertura, [Validators.required]));
            this.formCapital.get('cobetura')?.setValue(esOtroCapital ? 'otro' : this.poliza.capitalCobertura);
            this.formCapital.get('otroCapital')?.setValue(this.poliza.capitalCobertura);

            this.mostrarInputOtroCapital = esOtroCapital;

            if (esOtroCapital) {
                this.formCapital.controls['otroCapital'].setValidators([Validators.required, Validators.min(50000), Validators.max(this.maxCapital)]);
            } else {
                this.formCapital.controls['otroCapital'].clearValidators();
            }
            this.formCapital.controls['otroCapital'].updateValueAndValidity();

            this.validarCapitalSegunEdad();
        }
    }

    comprobarPolizaSesion() {
        if (!this.sesion.getPoliza()?.id) {
            this.router.navigate([ConstantesApp.PATH_FORBIDDEN]);
        } else {
            this.comprobarSesion = true;
            this.poliza.ultimaPantalla = ConstantesApp.PATH_COLECTIVOS_2_VER_PRECIO;
            this.eventService.publishEvent(AppEvents.actualizarUltimaAccion, this.poliza);
            this.eventService.publishEvent(AppEvents.actualizarColectivo, this.poliza.colectivo);

            this.seguroService.calcularPrecioPorPoliza(this.poliza.id!)
                .then(precioCalculado => {
                    this.poliza.precioMensual = precioCalculado!;
                    this.poliza.incluidoFallecimientoAccidente = true;
                    this.poliza.incluidoInvalidezAccidente = true;
                    this.sesion.setPoliza(this.poliza);
                    this.polizaService.actualizar(this.poliza);
                });
        }
    }

    public descargarPdfPresupuesto() {
        Utilidades.generarPdfColectivoCobertura(this.cliente, this.poliza, this.pdfService, this.datePipe).then((pdf) => {
            if (pdf) {
                this.eventService.publishEvent(AppEvents.previsualizarPDF, pdf);
            }
        });
    }

    abrirModalFallecimiento() {
        const dialogRef = this.dialog.open(ModalFallecimientoAccidenteComponent, {
            panelClass: 'modal-medium',
            data: { component: ModalFallecimientoAccidenteComponent, mostrarBtnFooter: false },
        });

        dialogRef.afterClosed().subscribe((result) => { });
    }

    abrirModalIncapacidad() {
        const dialogRef = this.dialog.open(ModalIncapacidadAccidenteComponent, {
            panelClass: 'modal-medium',
            data: { component: ModalIncapacidadAccidenteComponent, mostrarBtnFooter: false },
        });

        dialogRef.afterClosed().subscribe((result) => { });
    }

    abrirModalInformacionLegal() {
        const dialogRef = this.dialog.open(ModalInformacionLegalComponent, {
            panelClass: '',
            data: { component: ModalInformacionLegalComponent, mostrarBtnFooter: false },
        });

        dialogRef.afterClosed().subscribe((result) => { });
    }

    obtenerMaximoCapitalPermitido() {
        const edad = this.calcularEdad();
        if (edad >= 18 && edad <= 55) {
            return 300000;
        } else if (edad > 55 && edad <= 65) {
            return 150000;
        } else {
            return 0;
        }
    }

    calcularEdad(): number {
        const birthDate = new Date(this.cliente.fechaNacimiento);
        const currentDate = new Date();
        let age = currentDate.getFullYear() - birthDate.getFullYear();
        const m = currentDate.getMonth() - birthDate.getMonth();

        if (m < 0 || (m === 0 && currentDate.getDate() < birthDate.getDate())) {
            age--;
        }

        return age;
    }

    actualizarCapitalCobertura() {
        const cobertura = this.formCapital.controls['cobertura'].value;
        const otroCapitalFormControl = this.formCapital.controls['otroCapital']!;

        this.suscripcionOtroCapital?.unsubscribe();

        if (cobertura === 'otro') {
            this.mostrarInputOtroCapital = true;

            otroCapitalFormControl.setValidators([Validators.required, Validators.min(50000), Validators.max(this.maxCapital)]);
            otroCapitalFormControl.updateValueAndValidity();
        } else {
            this.mostrarInputOtroCapital = false;
            otroCapitalFormControl.setValue(null);
            otroCapitalFormControl.clearValidators();
            otroCapitalFormControl.updateValueAndValidity();

            this.poliza.capitalCobertura = parseFloat(cobertura);
            this.poliza.capitalAccidenteFallecimiento = parseFloat(cobertura) * 2;
            this.poliza.capitalAccidenteInvalidez = parseFloat(cobertura) * 2;
            this.calcularPrecioSeguro(this.poliza);
        }

        this.suscripcionOtroCapital = otroCapitalFormControl.valueChanges.pipe(
            debounceTime(800)
        ).subscribe(valor => {
            if (cobertura === 'otro' && valor) {
                if (otroCapitalFormControl.valid) {
                    this.poliza.capitalCobertura = parseFloat(valor);
                    this.poliza.capitalAccidenteFallecimiento = parseFloat(valor) * 2;
                    this.poliza.capitalAccidenteInvalidez = parseFloat(valor) * 2;
                    this.calcularPrecioSeguro(this.poliza);
                }
            }
        });
    }

    calcularPrecioSeguro(poliza: Poliza) {
        this.polizaService.actualizar(poliza).then((newPoliza: Poliza) => {

            this.seguroService.calcularPrecioPorPoliza(newPoliza.id!)
                .then((precioCalculado) => {
                    this.poliza.precioMensual = precioCalculado!;
                    this.polizaService.actualizar(poliza);

                    this.eventService.publishEvent(AppEvents.actualizarUltimaAccion, this.poliza);
                },
                    (error) => {
                        console.error('Error al calcular el precio del seguro', error);
                    }
                );
        });
    }

    validarCapitalSegunEdad() {
        const edad = this.calcularEdad();
        if (edad >= 18 && edad <= 55) {
            this.opcionesCapital = [...this.opcionesCapitalOriginales];
        } else if (edad > 55 && edad <= 65) {
            this.opcionesCapital = this.opcionesCapital.filter(c => c <= 150000);
        }

        if (this.formCapital.controls['cobertura'].value !== 'otro') {
            const maxCapital = edad <= 55 ? 300000 : (edad <= 65 ? 150000 : 0);
            if (this.formCapital.controls['cobertura'].value > maxCapital) {
                this.formCapital.controls['cobertura'].setValue(Math.max(...this.opcionesCapital));
            }
        } else {
            let maxCapital = edad >= 18 && edad <= 55 ? 300000 : 150000;
            if (this.formCapital.controls['otroCapital'].value > maxCapital) {
                this.formCapital.controls['otroCapital'].setValue(maxCapital);
            }
        }
    }

    validarCampos(): boolean {
        return this.formCapital.valid;
    }

    contratar() {
        if (this.validarCampos()) {

            if (this.formCapital.controls["cobertura"].value === 'otro') {
                this.poliza.capitalCobertura = this.formCapital.controls["otroCapital"].value;
            } else {
                this.poliza.capitalCobertura = this.formCapital.controls["cobertura"].value;
            }
            this.poliza.capitalAccidenteFallecimiento = this.poliza.capitalCobertura * 2;
            this.poliza.capitalAccidenteInvalidez = this.poliza.capitalCobertura * 2;

            this.router.navigateByUrl(ConstantesApp.PATH_COLECTIVOS_4_PREGUNTAS_COLECTIVOS);
        }
    }

    regresar(): void {
        this.router.navigate([ConstantesApp.PATH_COLECTIVOS_1_CALCULAR_SEGURO + '/' + this.poliza.colectivo?.url]);
    }
}
